import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import Sidebar from '../Sidebar';

const API_URL = process.env.REACT_APP_API_URL || '';

interface Client {
  id: string;
  name: string;
  displayName?: string;
  isCurrentClient?: boolean;
}

// Year Picker Component
interface YearPickerProps {
  value: number | undefined;
  onChange: (year: number) => void;
  disabled?: boolean;
}

const YearPicker: React.FC<YearPickerProps> = ({ value, onChange, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  const startYear = 1900;
  const years = Array.from({ length: currentYear - startYear + 1 }, (_, i) => currentYear - i);

  const handleYearSelect = (year: number) => {
    onChange(year);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={value || ''}
        readOnly
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm cursor-pointer bg-white"
        placeholder="Select year"
        disabled={disabled}
      />
      {!disabled && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      )}
      
      {isOpen && !disabled && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
            <div className="py-1">
              {years.map((year) => (
                <button
                  key={year}
                  type="button"
                  className={`block w-full text-left px-4 py-2 text-sm hover:bg-blue-50 hover:text-blue-700 ${
                    value === year ? 'bg-blue-100 text-blue-700' : 'text-gray-700'
                  }`}
                  onClick={() => handleYearSelect(year)}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const AddWaterCraftPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const fromPolicy = searchParams.get('fromPolicy') === 'true';
  const { quoteId } = useParams<{ quoteId: string }>();
  const clientId = location.state?.clientId || new URLSearchParams(location.search).get('clientId');
  const returnTo = location.state?.returnTo || new URLSearchParams(location.search).get('returnTo');
  const policyId = new URLSearchParams(location.search).get('policyId'); // ✅ ADD THIS
  
  // Determine if we're in master quote page mode (has returnTo URL) or create quote mode
  const isMasterQuoteMode = !!returnTo;
  
  console.log('📍 AddWaterCraftPage mounted:', { 
    quoteId, 
    clientId, 
    returnTo, 
    isMasterQuoteMode,
    policyId, // ✅ ADD THIS
    fromPolicy // ✅ ADD THIS
  });
  
  const [user, setUser] = useState<any>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    year: undefined as number | undefined,
    craftType: '',
    hullMake: '',
    hullLength: undefined as number | undefined,
    yearModel: undefined as number | undefined,
    bodyPaint: '',
    outboardMotorMake: '',
    outboardEngineNumber: '',
    horsePower: undefined as number | undefined,
    maxSpeed: undefined as number | undefined,
    vesselName: '',
    vesselStorage: '',
    vesselUsage: '',
    amateurBuilt: false,
    registeredOwner: '',
    financeHouse: '',
    financeBranch: '',
    hullValue: undefined as number | undefined,
    outboardValue: undefined as number | undefined,
    sailsValue: undefined as number | undefined,
    totalSumInsured: undefined as number | undefined,
    totalMonthlyPremium: undefined as number | undefined,
    trailerMake: '',
    trailerModel: '',
    trailerRegistration: '',
    trailerValue: undefined as number | undefined,
    trailerPremium: undefined as number | undefined,
  });
  
  const [errors, setErrors] = useState({
    year: '',
    craftType: '',
    hullMake: '',
    hullLength: '',
    vesselName: '',
    totalSumInsured: '',
  });
  
  // Currency display state
  const [currencyDisplay, setCurrencyDisplay] = useState({
    hullValue: '',
    outboardValue: '',
    sailsValue: '',
    totalSumInsured: '',
    totalMonthlyPremium: '',
    trailerValue: '',
    trailerPremium: '',
  });
  
  // Currency formatting functions
  const formatCurrencyInput = (value: string | number): string => {
    if (!value && value !== 0) return '';
    const stringValue = typeof value === 'number' ? value.toString() : value;
    const numericValue = stringValue.replace(/[^\d.]/g, '');
    const parts = numericValue.split('.');
    let wholePart = parts[0] || '';
    let decimalPart = parts[1] || '';
    
    if (decimalPart.length > 2) {
      decimalPart = decimalPart.substring(0, 2);
    }
    
    if (wholePart) {
      wholePart = parseInt(wholePart, 10).toLocaleString('en-US');
    }
    
    return decimalPart ? `${wholePart}.${decimalPart}` : wholePart;
  };
  
  const parseCurrencyInput = (value: string): number => {
    if (!value) return 0;
    return parseFloat(value.replace(/,/g, ''));
  };

  // ADD THIS HELPER FUNCTION RIGHT HERE
    const formatDriverLicensesForDisplay = (licenses: any): string => {
    console.log('🔍 formatDriverLicensesForDisplay input:', licenses);
    
    if (!licenses) return '';
    
    let licenseArray: any[] = [];
    
    if (Array.isArray(licenses)) {
      licenseArray = licenses;
    } else if (typeof licenses === 'string') {
      try {
        const parsed = JSON.parse(licenses);
        licenseArray = Array.isArray(parsed) ? parsed : [];
      } catch {
        // Not JSON, check for comma-separated or pipe-delimited
        if (licenses.includes('|')) {
          const parts = licenses.split('|');
          licenseArray = [{
            code: parts[0]?.trim() || '',
            dateOfFirstIssue: parts[1]?.trim() || '',
            expiresOn: parts[2]?.trim() || ''
          }];
        } else if (licenses.includes(',')) {
          licenseArray = licenses.split(',').map(code => ({
            code: code.trim(),
            dateOfFirstIssue: '',
            expiresOn: ''
          }));
        } else if (licenses.trim()) {
          licenseArray = [{ code: licenses.trim(), dateOfFirstIssue: '', expiresOn: '' }];
        }
      }
    } else if (licenses && typeof licenses === 'object') {
      // Single license object
      if (licenses.code) {
        licenseArray = [licenses];
      }
    }
    
    if (licenseArray.length === 0) return '';
    
    const validLicenses = licenseArray.filter(license => license && license.code && license.code.trim() !== '');
    if (validLicenses.length === 0) return '';
    
    const formattedLicenses = validLicenses.map(license => {
      const code = license.code?.trim() || '';
      const date = license.dateOfFirstIssue || license.date_of_first_issue || '';
      let formattedDate = '';
      if (date) {
        try {
          const dateObj = new Date(date);
          if (!isNaN(dateObj.getTime())) {
            formattedDate = dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
          } else {
            formattedDate = date;
          }
        } catch (error) {
          formattedDate = date;
        }
      }
      return formattedDate ? `${code} - ${formattedDate}` : code;
    }).filter(formatted => formatted.trim() !== '');
    
    if (formattedLicenses.length === 0) return '';
    const result = ` (${formattedLicenses.join(', ')})`;
    console.log('🔍 formatDriverLicensesForDisplay result:', result);
    return result;
  };
  
  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    };
    fetchUser();
  }, [navigate]);
  
  // Fetch existing water craft if editing
  useEffect(() => {
    const editId = new URLSearchParams(location.search).get('editId');
    if (editId && quoteId) {
      setEditingId(editId);
      fetchWaterCraftForEdit(editId);
    }
  }, [quoteId, location.search, policyId, fromPolicy]);

  // Re-fetch water craft data when clients are loaded to properly map registered owner
useEffect(() => {
  if (editingId && clients.length > 0) {
    console.log('🔄 Clients loaded, re-fetching water craft to map registered owner');
    fetchWaterCraftForEdit(editingId);
  }
}, [clients, editingId]);
  
  const fetchWaterCraftForEdit = async (id: string) => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(
      `${API_URL}/api/water-craft/${id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    const waterCraft = response.data.data || response.data;
    
    console.log('🔍 Fetched water craft registered_owner (stored name):', waterCraft.registered_owner);

// Helper function to find owner ID by matching name/display name
const findOwnerIdByDisplayName = (searchName: string): string => {
  if (!searchName || clients.length === 0) return '';
  
  // Try to find exact match in clients list
  const foundOwner = clients.find(client => {
    // Compare with displayName (includes license)
    if (client.displayName === searchName) return true;
    // Compare with name only
    if (client.name === searchName) return true;
    // Check if searchName is contained in displayName
    if (client.displayName?.toLowerCase().includes(searchName.toLowerCase())) return true;
    if (client.name?.toLowerCase().includes(searchName.toLowerCase())) return true;
    return false;
  });
  
  if (foundOwner) {
    console.log('✅ Found owner ID:', foundOwner.id, 'for name:', searchName);
    return foundOwner.id;
  }
  
  console.log('⚠️ Could not find owner ID for name:', searchName);
  return '';
};

let formattedRegisteredOwner = waterCraft.registered_owner?.toString() || '';

// Check if the stored value is a name (not an ID)
if (formattedRegisteredOwner && 
    isNaN(Number(formattedRegisteredOwner)) && 
    !formattedRegisteredOwner.startsWith('poi-')) {
  
  // Try to find the owner ID by the stored name
  const foundId = findOwnerIdByDisplayName(formattedRegisteredOwner);
  if (foundId) {
    formattedRegisteredOwner = foundId;
    console.log('📌 Mapped name to ID:', formattedRegisteredOwner);
  }
}
    
    setFormData({
      year: waterCraft.year,
      craftType: waterCraft.craft_type || '',
      hullMake: waterCraft.hull_make || '',
      hullLength: waterCraft.hull_length != null 
        ? Number(waterCraft.hull_length.toString().replace(/\.00$/, '')) 
        : undefined,
      yearModel: waterCraft.year_model,
      bodyPaint: waterCraft.body_paint || '',
      outboardMotorMake: waterCraft.outboard_motor_make || '',
      outboardEngineNumber: waterCraft.outboard_engine_number || '',
      horsePower: waterCraft.horse_power,
      maxSpeed: waterCraft.max_speed,
      vesselName: waterCraft.vessel_name || '',
      vesselStorage: waterCraft.vessel_storage || '',
      vesselUsage: waterCraft.vessel_usage || '',
      amateurBuilt: waterCraft.amateur_built || false,
      registeredOwner: formattedRegisteredOwner,
      financeHouse: waterCraft.finance_house || '',
      financeBranch: waterCraft.finance_branch || '',
      hullValue: waterCraft.hull_value,
      outboardValue: waterCraft.outboard_value,
      sailsValue: waterCraft.sails_value,
      totalSumInsured: waterCraft.total_sum_insured,
      totalMonthlyPremium: waterCraft.total_monthly_premium,
      trailerMake: waterCraft.trailer_make || '',
      trailerModel: waterCraft.trailer_model || '',
      trailerRegistration: waterCraft.trailer_registration || '',
      trailerValue: waterCraft.trailer_value,
      trailerPremium: waterCraft.trailer_premium,
    });
    
    setCurrencyDisplay({
      hullValue: formatCurrencyInput(waterCraft.hull_value?.toString() || ''),
      outboardValue: formatCurrencyInput(waterCraft.outboard_value?.toString() || ''),
      sailsValue: formatCurrencyInput(waterCraft.sails_value?.toString() || ''),
      totalSumInsured: formatCurrencyInput(waterCraft.total_sum_insured?.toString() || ''),
      totalMonthlyPremium: formatCurrencyInput(waterCraft.total_monthly_premium?.toString() || ''),
      trailerValue: formatCurrencyInput(waterCraft.trailer_value?.toString() || ''),
      trailerPremium: formatCurrencyInput(waterCraft.trailer_premium?.toString() || ''),
    });
    
  } catch (error) {
    console.error('Error fetching water craft for edit:', error);
  }
};
  
  // Fetch owners (clients and persons of interest)
      // Fetch owners (clients and persons of interest)
  useEffect(() => {
    const fetchOwners = async () => {
      setIsLoadingClients(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No authentication token found');

        // 1. Fetch current client details
        const clientResponse = await fetch(`${API_URL}/api/clients/${clientId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!clientResponse.ok) {
          throw new Error(`Failed to fetch current client: ${clientResponse.status}`);
        }

        const clientData = await clientResponse.json();
        
        // Get client's driver licenses - parse from various possible formats
        let clientLicenses: any[] = [];
        const licenseData = clientData.drivers_license_codes || clientData.drivers_license_code || clientData.driver_licenses;
        
        console.log('🔍 Client license data:', licenseData);
        
        if (licenseData) {
          if (Array.isArray(licenseData)) {
            clientLicenses = licenseData;
          } else if (typeof licenseData === 'string') {
            // Check if it's a JSON string
            if (licenseData.startsWith('[') || licenseData.startsWith('{')) {
              try {
                const parsed = JSON.parse(licenseData);
                clientLicenses = Array.isArray(parsed) ? parsed : [];
              } catch {
                clientLicenses = [];
              }
            } else if (licenseData.includes('|')) {
              // Handle pipe-delimited format: "CODE|ISSUE_DATE|EXPIRY_DATE"
              const parts = licenseData.split('|');
              clientLicenses = [{
                code: parts[0]?.trim() || '',
                dateOfFirstIssue: parts[1]?.trim() || '',
                expiresOn: parts[2]?.trim() || ''
              }];
            } else if (licenseData.includes(',')) {
              // Handle comma-separated codes
              clientLicenses = licenseData.split(',').map(code => ({
                code: code.trim(),
                dateOfFirstIssue: '',
                expiresOn: ''
              }));
            } else if (licenseData.trim()) {
              // Plain string license code
              clientLicenses = [{ code: licenseData.trim(), dateOfFirstIssue: '', expiresOn: '' }];
            }
          }
        }
        
        // Also check for driver_licenses array in client data
        if (clientData.driver_licenses && Array.isArray(clientData.driver_licenses)) {
          clientLicenses = [...clientLicenses, ...clientData.driver_licenses];
        }
        
        const clientLicenseString = formatDriverLicensesForDisplay(clientLicenses);
        
        // Build full name with first name and surname
        const clientFirstName = clientData.first_name || clientData.name || '';
        const clientSurname = clientData.last_name || clientData.surname || '';
        const clientFullName = `${clientFirstName} ${clientSurname}`.trim();
        const clientName = clientFullName || `Client ${clientId}`;
        
        const currentClient = {
          id: clientData.id?.toString() || clientId.toString(),
          name: clientName,
          displayName: `${clientName}${clientLicenseString}`,
          isCurrentClient: true
        };

        // 2. Fetch persons of interest for this client
        let personsOfInterest: any[] = [];
        try {
          const poiResponse = await fetch(`${API_URL}/api/persons-of-interest?clientId=${clientId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (poiResponse.ok) {
            const poiData = await poiResponse.json();
            console.log('🔍 POI data:', poiData);
            
            personsOfInterest = (poiData.data || poiData || []).map((poi: any) => {
              // Parse licenses for each person
              let licenses: any[] = [];
              const licenseData = poi.drivers_license_codes || poi.drivers_license_code || poi.driver_licenses;
              
              console.log(`🔍 POI ${poi.name} license data:`, licenseData);
              
              if (licenseData) {
                if (Array.isArray(licenseData)) {
                  licenses = licenseData;
                } else if (typeof licenseData === 'string') {
                  // Check if it's a JSON string
                  if (licenseData.startsWith('[') || licenseData.startsWith('{')) {
                    try {
                      const parsed = JSON.parse(licenseData);
                      licenses = Array.isArray(parsed) ? parsed : [];
                    } catch {
                      licenses = [];
                    }
                  } else if (licenseData.includes('|')) {
                    // Handle pipe-delimited format
                    const parts = licenseData.split('|');
                    licenses = [{
                      code: parts[0]?.trim() || '',
                      dateOfFirstIssue: parts[1]?.trim() || '',
                      expiresOn: parts[2]?.trim() || ''
                    }];
                  } else if (licenseData.includes(',')) {
                    // Handle comma-separated codes
                    licenses = licenseData.split(',').map(code => ({
                      code: code.trim(),
                      dateOfFirstIssue: '',
                      expiresOn: ''
                    }));
                  } else if (licenseData.trim()) {
                    licenses = [{ code: licenseData.trim(), dateOfFirstIssue: '', expiresOn: '' }];
                  }
                }
              }
              
              const licenseString = formatDriverLicensesForDisplay(licenses);
              const poiName = `${poi.name || ''} ${poi.surname || ''}`.trim() || poi.name || poi.surname || `Person of Interest ${poi.id}`;
              
              return {
                id: `poi-${poi.id}`,
                name: poiName,
                displayName: `${poiName}${licenseString}`,
              };
            }).filter((poi: any) => poi.id && poi.name);
          }
        } catch (poiError) {
          console.warn('Could not fetch persons of interest:', poiError);
        }

        // 3. Combine current client and persons of interest
        const allOwners: Client[] = [
          currentClient,
          ...personsOfInterest
        ].filter(item => item.id && item.name);

        console.log('🔍 Final owners list with display names:', allOwners);
        setClients(allOwners);

      } catch (error) {
        console.error('Error fetching owners:', error);
        setClients([]);
      } finally {
        setIsLoadingClients(false);
      }
    };

    fetchOwners();
  }, [clientId]);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    
    if (type !== 'checkbox') {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  const handleYearChange = (year: number, field: 'year' | 'yearModel') => {
    setFormData(prev => ({
      ...prev,
      [field]: year,
    }));
    if (field === 'year') {
      setErrors(prev => ({ ...prev, year: '' }));
    }
  };
  
  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: keyof typeof currencyDisplay) => {
    const { value } = e.target;
    const formattedValue = formatCurrencyInput(value);
    const numericValue = parseCurrencyInput(value);
    
    setFormData(prev => ({
      ...prev,
      [fieldName]: numericValue,
    }));
    
    setCurrencyDisplay(prev => ({
      ...prev,
      [fieldName]: formattedValue,
    }));
  };
  
  const handleCurrencyBlur = (fieldName: keyof typeof currencyDisplay) => {
    const currentValue = currencyDisplay[fieldName];
    if (currentValue) {
      const numericValue = parseCurrencyInput(currentValue);
      if (numericValue > 0) {
        const formatted = formatCurrencyInput(numericValue.toFixed(2));
        setCurrencyDisplay(prev => ({ ...prev, [fieldName]: formatted }));
      }
    }
  };
  
  const validateForm = () => {
    const newErrors = {
      year: !formData.year ? 'Year is required' : '',
      craftType: !formData.craftType ? 'Craft type is required' : '',
      hullMake: !formData.hullMake ? 'Hull make is required' : '',
      hullLength: !formData.hullLength ? 'Hull length is required' : '',
      vesselName: !formData.vesselName ? 'Vessel name is required' : '',
      totalSumInsured: !formData.totalSumInsured ? 'Total sum insured is required' : '',
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (isSubmitting) return;
  if (!validateForm()) return;
  
  setIsSubmitting(true);
  setSaveMessage('Saving water craft...');
  
  // Process registered owner to remove 'poi-' prefix if present
  let processedRegisteredOwner = formData.registeredOwner;
  if (processedRegisteredOwner && processedRegisteredOwner.startsWith('poi-')) {
    processedRegisteredOwner = processedRegisteredOwner.replace('poi-', '');
  }
  
  const submitData = {
    year: formData.year,
    craft_type: formData.craftType,
    hull_make: formData.hullMake,
    hull_length: formData.hullLength,
    year_model: formData.yearModel,
    body_paint: formData.bodyPaint,
    outboard_motor_make: formData.outboardMotorMake,
    outboard_engine_number: formData.outboardEngineNumber,
    horse_power: formData.horsePower,
    max_speed: formData.maxSpeed,
    vessel_name: formData.vesselName,
    vessel_storage: formData.vesselStorage,
    vessel_usage: formData.vesselUsage,
    amateur_built: formData.amateurBuilt,
    registered_owner: processedRegisteredOwner,
    finance_house: formData.financeHouse,
    finance_branch: formData.financeBranch,
    hull_value: formData.hullValue || 0,
    outboard_value: formData.outboardValue || 0,
    sails_value: formData.sailsValue || 0,
    total_sum_insured: formData.totalSumInsured || 0,
    total_monthly_premium: formData.totalMonthlyPremium || 0,
    trailer_make: formData.trailerMake,
    trailer_model: formData.trailerModel,
    trailer_registration: formData.trailerRegistration,
    trailer_value: formData.trailerValue || 0,
    trailer_premium: formData.trailerPremium || 0,
    client_id: parseInt(clientId || '0'),
    quote_id: quoteId ? parseInt(quoteId) : null
  };
  
  try {
    const token = localStorage.getItem('token');
    const cleanQuoteId = quoteId ? parseInt(quoteId) : null;
    
    if (editingId) {
      await axios.put(`${API_URL}/api/water-craft/${editingId}`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaveMessage('Water craft updated successfully!');
      
      // ✅ LOG ACTIVITY: Water Craft Updated
      if (cleanQuoteId) {
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const performedBy = user?.username || user?.email || 'System';
          
          await axios.post(
            `${API_URL}/api/quote-activity-logs`,
            {
              quote_id: cleanQuoteId,
              action_type: 'product_updated',
              description: `Water craft updated: ${formData.vesselName || ''}`.trim() || 'Water Craft',
              metadata: {
                productType: 'water_craft',
                productId: editingId,
                performedBy: performedBy,
                vesselName: formData.vesselName || '',
                craftType: formData.craftType || '',
                hullMake: formData.hullMake || '',
                hullLength: formData.hullLength || '',
                totalSumInsured: formData.totalSumInsured || 0,
                totalMonthlyPremium: formData.totalMonthlyPremium || 0,
                updatedAt: new Date().toISOString()
              }
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          console.log('✅ Activity logged: water_craft_updated');
        } catch (logError) {
          console.error('❌ Failed to log water craft update:', logError);
          // Don't fail the update if logging fails
        }
      }
      
      // ✅ FIX: Navigate based on mode for edit as well
      setTimeout(() => {
        const validClientId = clientId || new URLSearchParams(location.search).get('clientId');
        
        if (!validClientId) {
          console.error('❌ No clientId available for navigation');
          setSaveMessage('Error: Missing client ID');
          setIsSubmitting(false);
          return;
        }
        
        if (isMasterQuoteMode && returnTo) {
          const decodedReturnUrl = decodeURIComponent(returnTo);
          const separator = decodedReturnUrl.includes('?') ? '&' : '?';
          const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=water_craft&clientId=${validClientId}`;
          console.log('📍 Returning to master quote page after edit:', finalUrl);
          navigate(finalUrl);
        } else if (quoteId && !isNaN(parseInt(quoteId))) {
          navigate(`/create-quote/${validClientId}`, { 
            state: { refreshProducts: true, activeTab: 'quotes' } 
          });
        } else {
          navigate(`/create-quote/${validClientId}`, { 
            state: { refreshProducts: true } 
          });
        }
      }, 1500);
      
      return; // Important: exit after setting timeout to prevent double navigation
    } else {
      await axios.post(`${API_URL}/api/water-craft`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaveMessage('Water craft added successfully!');
      
      // ✅ LOG ACTIVITY: Water Craft Added
      if (cleanQuoteId) {
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const performedBy = user?.username || user?.email || 'System';
          
          await axios.post(
            `${API_URL}/api/quote-activity-logs`,
            {
              quote_id: cleanQuoteId,
              action_type: 'product_added',
              description: `Water craft added: ${formData.vesselName || ''}`.trim() || 'Water Craft',
              metadata: {
                productType: 'water_craft',
                performedBy: performedBy,
                vesselName: formData.vesselName || '',
                craftType: formData.craftType || '',
                hullMake: formData.hullMake || '',
                hullLength: formData.hullLength || '',
                totalSumInsured: formData.totalSumInsured || 0,
                totalMonthlyPremium: formData.totalMonthlyPremium || 0,
                addedAt: new Date().toISOString()
              }
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          console.log('✅ Activity logged: water_craft_added');
        } catch (logError) {
          console.error('❌ Failed to log water craft addition:', logError);
          // Don't fail the addition if logging fails
        }
      }
      
      // Save to localStorage draft for new quotes (not editing)
      // ✅ Determine the storage key based on context
      let storageKey: string;
      
      // ✅ Only use policy key if we're explicitly coming from policy
      if (policyId && fromPolicy === true) {
        // Coming from Policy Detail page - save with policy ID key
        storageKey = `policy_${policyId}_watercraft`;
        console.log(`📂 Saving water craft with policy key: ${storageKey}`);
      } else {
        // Coming from Quote/Create Quote page - save with client draft key
        storageKey = `client_${clientId}_water_crafts_draft`;
        console.log(`📂 Saving water craft with client draft key: ${storageKey}`);
      }
      
      const existingDrafts = localStorage.getItem(storageKey);
      let waterCraftsDraft = existingDrafts ? JSON.parse(existingDrafts) : [];
      
      // Check for duplicate
      const isDuplicate = waterCraftsDraft.some((wc: any) => 
        wc.vesselName === formData.vesselName && 
        wc.hullMake === formData.hullMake
      );
      
      if (!isDuplicate) {
        const newWaterCraft = {
          year: formData.year,
          craftType: formData.craftType,
          hullMake: formData.hullMake,
          hullLength: formData.hullLength,
          yearModel: formData.yearModel,
          bodyPaint: formData.bodyPaint,
          outboardMotorMake: formData.outboardMotorMake,
          outboardEngineNumber: formData.outboardEngineNumber,
          horsePower: formData.horsePower,
          maxSpeed: formData.maxSpeed,
          vesselName: formData.vesselName,
          vesselStorage: formData.vesselStorage,
          vesselUsage: formData.vesselUsage,
          amateurBuilt: formData.amateurBuilt,
          registeredOwner: formData.registeredOwner,
          financeHouse: formData.financeHouse,
          financeBranch: formData.financeBranch,
          hullValue: formData.hullValue,
          outboardValue: formData.outboardValue,
          sailsValue: formData.sailsValue,
          totalSumInsured: formData.totalSumInsured,
          totalMonthlyPremium: formData.totalMonthlyPremium,
          trailerMake: formData.trailerMake,
          trailerModel: formData.trailerModel,
          trailerRegistration: formData.trailerRegistration,
          trailerValue: formData.trailerValue,
          trailerPremium: formData.trailerPremium,
          id: `temp-${Date.now()}`,
          isTemp: true,
          status: 'active',
          inception_date: new Date().toISOString().split('T')[0],
          product_type: 'water-craft'
        };
        
        waterCraftsDraft.push(newWaterCraft);
        localStorage.setItem(storageKey, JSON.stringify(waterCraftsDraft));
        console.log(`💾 Saved water craft to localStorage with key: ${storageKey}`);
      }
    }

    // ✅ FIX: Navigate based on mode
    setTimeout(() => {
      const validClientId = clientId || new URLSearchParams(location.search).get('clientId');
      
      if (!validClientId) {
        console.error('❌ No clientId available for navigation');
        setSaveMessage('Error: Missing client ID');
        setIsSubmitting(false);
        return;
      }
      
      if (isMasterQuoteMode && returnTo) {
        // Master quote mode - navigate back to master quote page
        const decodedReturnUrl = decodeURIComponent(returnTo);
        const separator = decodedReturnUrl.includes('?') ? '&' : '?';
        const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=water_craft&clientId=${validClientId}`;
        console.log('📍 Returning to master quote page:', finalUrl);
        navigate(finalUrl);
      } else if (quoteId && !isNaN(parseInt(quoteId))) {
        // Create quote mode but with quoteId - navigate to create quote page
        navigate(`/create-quote/${validClientId}`, { 
          state: { refreshProducts: true, activeTab: 'quotes' } 
        });
      } else {
        // Fallback
        navigate(`/create-quote/${validClientId}`, { 
          state: { refreshProducts: true } 
        });
      }
    }, 1500);
      
  } catch (error: any) {
    console.error('Error saving water craft:', error);
    setSaveMessage(error.response?.data?.message || 'Failed to save water craft');
    setIsSubmitting(false);
  }
};
  
  if (!user) return <div>Loading...</div>;
  
  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title={editingId ? 'Edit Water Craft' : 'Add Water Craft'}
          user={user}
          toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={true}
        />
        <div className="flex-1 p-6">
          <div className="max-w-4xl mx-auto">

            <button
  onClick={() => {
    console.log('🔍 Back button clicked - debug:', { 
      isMasterQuoteMode, 
      returnTo, 
      clientId,
      quoteId,
      editingId,
      policyId,
      fromPolicy
    });
    
    if (policyId && fromPolicy) {
      // Return to policy detail page
      const returnUrl = returnTo || `/policy/${policyId}`;
      const separator = returnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${returnUrl}${separator}refreshProducts=true&productAdded=water_craft&clientId=${clientId}`;
      console.log('📍 Back button - returning to policy detail page:', finalUrl);
      navigate(finalUrl);
    } else if (isMasterQuoteMode && returnTo) {
      // Go back to master quote page with refresh flag
      const decodedReturnUrl = decodeURIComponent(returnTo);
      const separator = decodedReturnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=water_craft&clientId=${clientId}`;
      console.log('📍 Back button - returning to master quote page with refresh:', finalUrl);
      navigate(finalUrl);
    } else {
      console.log('📍 Back button - going to create quote page (master mode false or no returnTo)');
      // Go back to create quote page
      navigate(`/create-quote/${clientId}`);
    }
  }}
  className="mb-4 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
>
  ← {fromPolicy ? 'Back to Policy Details' : 'Back to Quote'}
</button>
            
            <div className="relative bg-white p-6 rounded-lg shadow-xl w-full max-w-4xl">
              <h3 className="text-xl font-bold mb-4">
                {editingId ? 'Edit Water Craft' : 'Add Water Craft'}
              </h3>
              
              {saveMessage && (
                <div className={`${saveMessage.includes('❌') ? 'bg-red-100 border-red-400 text-red-700' : 'bg-green-100 border-green-400 text-green-700'} border px-4 py-3 rounded relative mb-4`}>
                  {saveMessage}
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div className="space-y-6 mb-6">
                  
                  {/* Basic Information */}
                  <div className="border-b pb-4">
                    <h4 className="text-lg font-semibold mb-3">Basic Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Year *</label>
                        <YearPicker
                          value={formData.year}
                          onChange={(year) => handleYearChange(year, 'year')}
                          disabled={false}
                        />
                        {errors.year && <p className="text-red-500 text-xs mt-1">{errors.year}</p>}
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Type of Craft *</label>
                        <select
                          name="craftType"
                          value={formData.craftType}
                          onChange={handleInputChange}
                          className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                        >
                          <option value="">Select craft type</option>
                          <option value="sail">Sail</option>
                          <option value="ski">Ski</option>
                          <option value="motor">Motor</option>
                        </select>
                        {errors.craftType && <p className="text-red-500 text-xs mt-1">{errors.craftType}</p>}
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Make of Hull *</label>
                        <input
                          type="text"
                          name="hullMake"
                          value={formData.hullMake}
                          onChange={handleInputChange}
                          className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                        />
                        {errors.hullMake && <p className="text-red-500 text-xs mt-1">{errors.hullMake}</p>}
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Length of Hull (meters) *</label>
                        <input
                          type="number"
                          step="0.1"
                          name="hullLength"
                          value={
                            formData.hullLength !== undefined && formData.hullLength !== null
                              ? formData.hullLength.toString().replace(/\.00$/, '')
                              : ''
                          }
                          onChange={handleInputChange}
                          className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        {errors.hullLength && <p className="text-red-500 text-xs mt-1">{errors.hullLength}</p>}
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Year Model</label>
                        <YearPicker
                          value={formData.yearModel}
                          onChange={(year) => handleYearChange(year, 'yearModel')}
                          disabled={false}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Body Paint</label>
                        <select
                          name="bodyPaint"
                          value={formData.bodyPaint}
                          onChange={handleInputChange}
                          className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                        >
                          <option value="">Select paint type</option>
                          <option value="glitter">Glitter</option>
                          <option value="plain">Plain</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  
                  {/* Motor Information */}
                  <div className="border-b pb-4">
                    <h4 className="text-lg font-semibold mb-3">Motor Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Make and Model of Outboard Motor</label>
                        <input
                          type="text"
                          name="outboardMotorMake"
                          value={formData.outboardMotorMake}
                          onChange={handleInputChange}
                          className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Outboard/Engine Number</label>
                        <input
                          type="text"
                          name="outboardEngineNumber"
                          value={formData.outboardEngineNumber}
                          onChange={handleInputChange}
                          className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Horse Power</label>
                        <input
                          type="number"
                          name="horsePower"
                          value={formData.horsePower || ''}
                          onChange={handleInputChange}
                          className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Max Speed (km/h)</label>
                        <input
                          type="number"
                          name="maxSpeed"
                          value={formData.maxSpeed || ''}
                          onChange={handleInputChange}
                          className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Vessel Information */}
                  <div className="border-b pb-4">
                    <h4 className="text-lg font-semibold mb-3">Vessel Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Name of Vessel *</label>
                        <input
                          type="text"
                          name="vesselName"
                          value={formData.vesselName}
                          onChange={handleInputChange}
                          className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                        />
                        {errors.vesselName && <p className="text-red-500 text-xs mt-1">{errors.vesselName}</p>}
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Where will vessel be kept?</label>
                        <input
                          type="text"
                          name="vesselStorage"
                          value={formData.vesselStorage}
                          onChange={handleInputChange}
                          className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Where will vessel be used?</label>
                        <input
                          type="text"
                          name="vesselUsage"
                          value={formData.vesselUsage}
                          onChange={handleInputChange}
                          className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                        />
                      </div>
                      
                      <div className="col-span-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            name="amateurBuilt"
                            checked={formData.amateurBuilt}
                            onChange={handleInputChange}
                            className="mr-2"
                          />
                          Was vessel built by an amateur?
                        </label>
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Registered Owner</label>
                        <select
                          name="registeredOwner"
                          value={formData.registeredOwner}
                          onChange={handleInputChange}
                          className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                          disabled={isLoadingClients}
                        >
                          <option value="" disabled>Select an owner</option>
                          {isLoadingClients ? (
                            <option value="" disabled>Loading owners...</option>
                          ) : clients.length === 0 ? (
                            <option value="" disabled>No owners available</option>
                          ) : (
                            clients.map(client => (
                              <option key={client.id} value={client.id}>
                                {client.displayName || client.name}
                              </option>
                            ))
                          )}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Finance House</label>
                        <input
                          type="text"
                          name="financeHouse"
                          value={formData.financeHouse}
                          onChange={handleInputChange}
                          className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Branch</label>
                        <input
                          type="text"
                          name="financeBranch"
                          value={formData.financeBranch}
                          onChange={handleInputChange}
                          className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Insurance Values */}
                  <div className="border-b pb-4">
                    <h4 className="text-lg font-semibold mb-3">Insurance Values</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Value of Hull and Fixed Equipment</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500">R</span>
                          </div>
                          <input
                            type="text"
                            value={currencyDisplay.hullValue}
                            onChange={(e) => handleCurrencyChange(e, 'hullValue')}
                            onBlur={() => handleCurrencyBlur('hullValue')}
                            className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Value of Outboard Motors</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500">R</span>
                          </div>
                          <input
                            type="text"
                            value={currencyDisplay.outboardValue}
                            onChange={(e) => handleCurrencyChange(e, 'outboardValue')}
                            onBlur={() => handleCurrencyBlur('outboardValue')}
                            className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Value of Sails, Masts and Rigging</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500">R</span>
                          </div>
                          <input
                            type="text"
                            value={currencyDisplay.sailsValue}
                            onChange={(e) => handleCurrencyChange(e, 'sailsValue')}
                            onBlur={() => handleCurrencyBlur('sailsValue')}
                            className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Total Sum Insured *</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500">R</span>
                          </div>
                          <input
                            type="text"
                            value={currencyDisplay.totalSumInsured}
                            onChange={(e) => handleCurrencyChange(e, 'totalSumInsured')}
                            onBlur={() => handleCurrencyBlur('totalSumInsured')}
                            className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                            placeholder="0.00"
                          />
                        </div>
                        {errors.totalSumInsured && <p className="text-red-500 text-xs mt-1">{errors.totalSumInsured}</p>}
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Total Monthly Premium</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500">R</span>
                          </div>
                          <input
                            type="text"
                            value={currencyDisplay.totalMonthlyPremium}
                            onChange={(e) => handleCurrencyChange(e, 'totalMonthlyPremium')}
                            onBlur={() => handleCurrencyBlur('totalMonthlyPremium')}
                            className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Trailer Information */}
                  <div>
                    <h4 className="text-lg font-semibold mb-3">Trailer Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Trailer Make</label>
                        <input
                          type="text"
                          name="trailerMake"
                          value={formData.trailerMake}
                          onChange={handleInputChange}
                          className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Trailer Model</label>
                        <input
                          type="text"
                          name="trailerModel"
                          value={formData.trailerModel}
                          onChange={handleInputChange}
                          className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Trailer Registration Number</label>
                        <input
                          type="text"
                          name="trailerRegistration"
                          value={formData.trailerRegistration}
                          onChange={handleInputChange}
                          className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Trailer Value</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500">R</span>
                          </div>
                          <input
                            type="text"
                            value={currencyDisplay.trailerValue}
                            onChange={(e) => handleCurrencyChange(e, 'trailerValue')}
                            onBlur={() => handleCurrencyBlur('trailerValue')}
                            className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-bold mb-2 text-sm">Trailer Monthly Premium</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500">R</span>
                          </div>
                          <input
                            type="text"
                            value={currencyDisplay.trailerPremium}
                            onChange={(e) => handleCurrencyChange(e, 'trailerPremium')}
                            onBlur={() => handleCurrencyBlur('trailerPremium')}
                            className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                </div>
                
                <div className="flex justify-end space-x-4">

                  <button
  type="button"
  onClick={() => {
    if (policyId && fromPolicy) {
      // Return to policy detail page
      const returnUrl = returnTo || `/policy/${policyId}`;
      const separator = returnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${returnUrl}${separator}refreshProducts=true&productAdded=water_craft&clientId=${clientId}`;
      console.log('📍 Cancel button - returning to policy detail page:', finalUrl);
      navigate(finalUrl);
    } else if (isMasterQuoteMode && returnTo) {
      // Go back to master quote page with refresh flag
      const decodedReturnUrl = decodeURIComponent(returnTo);
      const separator = decodedReturnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=water_craft&clientId=${clientId}`;
      console.log('📍 Cancel button - returning to master quote page with refresh:', finalUrl);
      navigate(finalUrl);
    } else {
      navigate(`/create-quote/${clientId}`);
    }
  }}
  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded text-sm"
>
  Cancel
</button>

                  <button
                    type="submit"
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : (editingId ? 'Update Water Craft' : 'Save Water Craft')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddWaterCraftPage;